import React, { createContext, useContext, ReactNode, useEffect, useRef } from "react";
import { AppTranslationContext } from "@type/provider/Translation";
import useRoute from "@hooks/useNextRoute";
import "@translations/i18n";
import { Trans, useTranslation } from "react-i18next";
import _ from "lodash";

export const TranslationContext = createContext<AppTranslationContext>({
  i18n: {
    t: () => {},
  },
  Trans: () => {
    return <></>;
  },
});

interface TypeUseTranslationContext {
  (): AppTranslationContext;
}

const TranslationProvider = ({
  children,
  pageContext,
}: AppTranslationContext): ReactNode => {
  const { t, i18n } = useTranslation();
  const router = useRoute();
  
  // Prefer pageContext.locale (from server) over router.query.locale for consistency
  const pageLocale = _.get(pageContext, "locale");
  const routerLocale = _.get(router, "query.locale", "en");
  const locale: any = pageLocale || routerLocale;
  
  // Track if we've set the initial language to prevent unnecessary changes
  const hasSetInitialLanguage = useRef(false);

  // Handle locale changes via useEffect for subsequent updates
  useEffect(() => {
    if (locale && locale !== i18n.language && hasSetInitialLanguage.current) {
      i18n.changeLanguage(locale);
    }
  }, [locale, i18n]);

  // Change language synchronously if needed (before first render)
  // This is safe because i18next's changeLanguage can be called synchronously
  // and we're ensuring server/client consistency by using pageContext.locale
  if (locale && locale !== i18n.language && !hasSetInitialLanguage.current) {
    i18n.changeLanguage(locale);
    hasSetInitialLanguage.current = true;
  }

  const i18nModel = {
    t: t,
    changeLanguage: i18n.changeLanguage,
    language: i18n.language,
  };

  return (
    <TranslationContext.Provider value={{ i18n: i18nModel, Trans }}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslationContext: TypeUseTranslationContext = () => {
  return useContext(TranslationContext);
};

export default TranslationProvider;
